import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../services/api';

interface FiscalSponsor {
  id: string;
  name: string;
  ein: string;
  address: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  // Add other properties as needed
}

interface FiscalSponsorState {
  fiscalSponsor: FiscalSponsor | null;
  loading: boolean;
  userExists: boolean;
  userId: string | null;
  error: string | undefined;
}

export const useFiscalSponsor = () => {
  const [state, setState] = useState<FiscalSponsorState>({
    fiscalSponsor: null,
    loading: true,
    userExists: false,
    userId: null,
    error: undefined  // Changed from null to undefined to match the interface
  });

  const { user, getToken } = useAuth();

  const fetchFiscalSponsor = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const token = await getToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // Check if user has a fiscal sponsor
      const response = await api.fiscalSponsor.getFiscalSponsor();

      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          loading: false,
          fiscalSponsor: response.data,
          userExists: true,
          userId: user.id,
          error: undefined  // Changed from null to undefined to match the interface
        }));
      } else {
        throw new Error(response.data.message || 'No fiscal sponsor found');
      }
    } catch (error: any) {  // Explicitly typed as any to handle the error.message access
      console.error('Error fetching fiscal sponsor:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load fiscal sponsor',
        fiscalSponsor: null
      }));
    }
  }, [user, getToken]);

  useEffect(() => {
    fetchFiscalSponsor();
  }, [fetchFiscalSponsor]);

  const createFiscalSponsor = useCallback(async (data: any) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await api.fiscalSponsor.createFiscalSponsor(data);
      
      if (response.success) {
        setState(prev => ({
          ...prev,
          loading: false,
          fiscalSponsor: response.data,
          userExists: true,
          userId: user?.id || null,
          error: undefined
        }));
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create fiscal sponsor');
      }
    } catch (error: any) {  // Explicitly typed as any
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to create fiscal sponsor'
      }));
      throw error;
    }
  }, [user]);

  const updateFiscalSponsor = useCallback(async (data: any) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await api.fiscalSponsor.updateFiscalSponsor(data);
      
      if (response.success) {
        setState(prev => ({
          ...prev,
          loading: false,
          fiscalSponsor: response.data,
          error: undefined
        }));
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update fiscal sponsor');
      }
    } catch (error: any) {  // Explicitly typed as any
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update fiscal sponsor'
      }));
      throw error;
    }
  }, []);

  return {
    ...state,
    createFiscalSponsor,
    updateFiscalSponsor,
    refreshFiscalSponsor: fetchFiscalSponsor
  };
}; 